import SelectAllColumns from '@Component/EmployeeTable/TableStates/SelectAllColumns.tsx';
import { TableEmptyCreatePension } from '@Component/EmployeeTable/TableStates/TableEmptyCreatePension.tsx';
import { TableEmptyCreateReimbursement } from '@Component/EmployeeTable/TableStates/TableEmptyCreateReimbursement.tsx';
import { TableEmptyCreateSalary } from '@Component/EmployeeTable/TableStates/TableEmptyCreateSalary.tsx';
import { NewTableEmptyResetFilter } from '@Component/EmployeeTable/TableStates/TableEmptyResetFilter.tsx';
import { addPeriodToUrl, FilterState } from '@Component/Filter/utils';
import { SkeletonRows } from '@Component/LoadMore/SkeletonRows';
import { DeleteModal } from '@Component/Modals/DeleteModal/DeleteModal';
import { LoadMoreTargetRow, TlxNumberCell } from '@Component/Table/Table.utils.tsx';
import { ColumnType, OrderByTypeSalaryOverview, useTableSorting } from '@Hooks/useOrdering';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext.ts';
import { AmeldingOverviewStatus, OverviewDTO } from '@Models/Overview.types.ts';
import { DeleteStatus, useDeleteVoucher } from '@Page/Overview/actions/useDeleteVoucher.ts';
import { DisplayName } from '@Page/Overview/DisplayName';
import { getColumns, getDefaultColumns, OVERVIEW_TABLE_COLUMNS_SETTING_KEY } from '@Page/Overview/DisplayOptions';
import { useOverviewVouchers } from '@Page/Overview/hooks/useOverviewVouchers';
import { OverviewTopBar } from '@Page/Overview/OverviewTopBar';
import { useIndeterminateCheckbox } from '@Page/SalaryRun/hooks/useIndeterminateCheckbox.ts';
import { SelectAllState } from '@Page/SalaryRun/stateReducer.ts';
import {
    AvatarById,
    getContextId,
    HTTPError,
    ListResponse,
    ResponseWrapper,
    UseFetchPaginatedState,
    useSearchParam,
    useTableColumnsSetting,
} from '@tlx/astro-shared';
import {
    ActionsCell,
    ActionsColumn,
    ApprovedIcon,
    AttachmentIcon,
    Button,
    Cell,
    Column,
    DeleteIcon,
    Header,
    KebabIcon,
    PopupMenu,
    PopupMenuItemButton,
    Row,
    Status,
    StatusVariant,
    Table,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    UnapproveIcon,
    usePopupMenu,
} from '@tlx/atlas';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api';
import { dateUtil } from '@Util/date.ts';
import { APIUrls } from '@Util/urls';
import React, { Key, MutableRefObject, useEffect, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { KeyedMutator } from 'swr';
import './OverviewTable.css';
import { useSalarySettings } from '@Page/Onboarding/hooks/useSalarySettings.ts';

interface OverviewTableProps {
    scrollableContainerRef: MutableRefObject<HTMLDivElement | null>;
    filterState: FilterState;
    setSelectedPdfItem: (selectedPdfItem: { voucherId: number; transactionId: number; isPayslip: boolean }) => void;
}

export const OverviewTable: React.FC<OverviewTableProps> = ({
    scrollableContainerRef,
    filterState: filters,
    setSelectedPdfItem,
}) => {
    const intl = useIntl();

    const sorting = useTableSorting<OrderByTypeSalaryOverview>();
    const { mutate, ...dto } = useOverviewVouchers({
        filters,
        sorting,
    });

    const { data: salarySettings } = useSalarySettings();
    const moduleSalaryApproval = salarySettings?.moduleSalaryApproval;

    const defaultColumns = getDefaultColumns(intl, filters, moduleSalaryApproval);

    const columnIds = useTableColumnsSetting(
        OVERVIEW_TABLE_COLUMNS_SETTING_KEY,
        defaultColumns.map((column) => column.id),
    );

    const columns = getColumns(intl, filters, moduleSalaryApproval)
        .filter((column) => columnIds.includes(column.id))
        .sort((a, b) => columnIds.indexOf(a.id) - columnIds.indexOf(b.id));

    return (
        <>
            <OverviewTopBar {...filters} />
            <Table
                density="compact"
                sort={sorting.sort}
                onSortChange={sorting.onSortChange}
                className={'salary-overview__table'}
            >
                <Header>
                    <Row>
                        <ColumnList columns={columns} />
                        <ActionsColumn className="sticky-action-column" />
                    </Row>
                </Header>
                <TableBodyWrapper
                    dto={dto}
                    columns={columns}
                    filters={filters}
                    scrollableContainerRef={scrollableContainerRef}
                    mutate={mutate as never}
                    setSelectedPdfItem={setSelectedPdfItem}
                />
            </Table>
        </>
    );
};

interface TableBodyWrapperProps {
    dto: UseFetchPaginatedState<OverviewDTO>;
    columns: ColumnType[];
    filters: FilterState;
    scrollableContainerRef: MutableRefObject<HTMLDivElement | null>;
    mutate: KeyedMutator<ListResponse<OverviewDTO>[]>;
    setSelectedPdfItem: (selectedPdfItem: { voucherId: number; transactionId: number; isPayslip: boolean }) => void;
}

function SalaryOverviewRowActions(props: {
    approvedByDisplayed: boolean;
    item: OverviewDTO;
    intl: IntlShape;
    handleApproveUnapprove: (response: ResponseWrapper<OverviewDTO> | undefined, error: HTTPError | undefined) => void;
    onClickDeleteVoucher: () => void;
    onClickViewPreviewPdf: (isPayslip: boolean) => void;
}) {
    const { openerButtonProps, popupMenuProps } = usePopupMenu();

    async function onClickApproveUnapprove() {
        const { response, error } = await callApi<ResponseWrapper<OverviewDTO>>(
            props.item.approvedBy === null
                ? computeUrl(
                      APIUrls.SALARY_OVERVIEW_APPROVE(props.item.id.toString()),
                      mapObjectToUrlSearchParams({
                          fields: '*, approvedBy(id,displayName,pictureId)',
                      }),
                  )
                : computeUrl(APIUrls.SALARY_OVERVIEW_UNAPPROVE(props.item.id.toString())),
            MethodType.PUT,
        );

        props.handleApproveUnapprove(response, error);
    }

    const [primaryButtonLoading, setPrimaryButtonLoading] = useState(false);

    const moduleSalaryApproval = useSalarySettings().data?.moduleSalaryApproval;

    const showUnApproveBtn =
        props.approvedByDisplayed && props.item.approvedBy !== null && props.item.paymentDate === null;
    return (
        <ActionsCell>
            {props.approvedByDisplayed && props.item.approvedBy === null && props.item.paymentDate === null && (
                <Button
                    key={`approve-${props.item.id}`}
                    data-testid={`approve-voucher-${props.item.id}`}
                    variant="tertiary"
                    loading={primaryButtonLoading}
                    onClick={async () => {
                        setPrimaryButtonLoading(true);
                        const start = performance.now();
                        await onClickApproveUnapprove();
                        const end = performance.now();
                        const timeMs = end - start;
                        if (timeMs < 200) {
                            setTimeout(() => {
                                setPrimaryButtonLoading(false);
                            }, 200 - timeMs);
                        } else {
                            setPrimaryButtonLoading(false);
                        }
                    }}
                >
                    <span>
                        <FormattedMessage id={'text_approve'} />
                    </span>
                    <ApprovedIcon />
                </Button>
            )}
            <Button
                data-testid="view-voucher"
                variant="icon"
                aria-label={props.intl.formatMessage({
                    id: 'text_view_voucher',
                })}
                onClick={() => {
                    // View voucher pdf
                    props.onClickViewPreviewPdf(false);
                }}
            >
                <AttachmentIcon />
            </Button>
            {(showUnApproveBtn || moduleSalaryApproval || props.item.completedLogId === 0) && (
                // Kebab menu if there is at least one action to show (unapprove, view payslips, delete)
                <Button {...openerButtonProps} data-testid="open-button" variant={'icon'}>
                    <KebabIcon />
                </Button>
            )}
            <PopupMenu {...popupMenuProps}>
                {showUnApproveBtn && (
                    <PopupMenuItemButton data-testid="" onClick={onClickApproveUnapprove}>
                        <UnapproveIcon />
                        <FormattedMessage id={'text_unapprove'} />
                    </PopupMenuItemButton>
                )}
                {moduleSalaryApproval && ( // Show the "View Payslips" in the kebab-popup menu only if the salary approval module is enabled
                    <PopupMenuItemButton
                        data-testid="view-payslips"
                        aria-label={props.intl.formatMessage({
                            id: 'text_view_payslips',
                        })}
                        onClick={() => props.onClickViewPreviewPdf(true)}
                    >
                        <AttachmentIcon />
                        <FormattedMessage id={'text_view_payslips'} />
                    </PopupMenuItemButton>
                )}
                {props.item.completedLogId === 0 && ( // Only show the delete option if the voucher is not completed
                    <PopupMenuItemButton
                        data-testid="delete-voucher"
                        aria-label={props.intl.formatMessage({
                            id: 'text_delete_voucher',
                        })}
                        onClick={props.onClickDeleteVoucher}
                    >
                        <DeleteIcon />
                        <FormattedMessage id={'text_delete_voucher'} />
                    </PopupMenuItemButton>
                )}
            </PopupMenu>
        </ActionsCell>
    );
}

export const TableBodyWrapper: React.FC<TableBodyWrapperProps> = ({
    dto,
    columns,
    filters,
    scrollableContainerRef,
    mutate,
    setSelectedPdfItem,
}) => {
    const intl = useIntl();
    const [confirmDeleteById, setConfirmDeleteById] = useState(0);
    const { deleteVoucher, deleteStatus, resetStatus } = useDeleteVoucher(mutate);

    function onConfirmDeleteVoucher() {
        deleteVoucher(confirmDeleteById);
    }

    useEffect(() => {
        if (deleteStatus === DeleteStatus.SUCCESS) {
            setConfirmDeleteById(0);
            resetStatus();
        }
    }, [deleteStatus]);

    const filtersApplied = filters.searchFilter.query !== '';
    const isEmpty = !dto.isLoading && dto.isEmpty;
    const emptyWithoutFilters = isEmpty && !filtersApplied;
    const emptyWithFilters = isEmpty && filtersApplied;

    const renderComponentBasedOnFilter = (filters: FilterState) => {
        const closestMonthPeriod = addPeriodToUrl(filters.periodFilter);
        switch (filters.contentFilter.query) {
            case 'pension':
                return <TableEmptyCreatePension />;
            case 'reimbursement':
                return <TableEmptyCreateReimbursement />;
            default:
                return <TableEmptyCreateSalary closestMonthPeriod={closestMonthPeriod} />;
        }
    };

    const isApprovedByDisplayed = columns.find((column) => column.id == 'approvedBy') != undefined;

    const [successItemId, setSuccessItemId] = useState(0);

    function handleSuccess(id: number) {
        setSuccessItemId(id);
        // Reset the success item ID after 2 seconds to remove the highlight
        setTimeout(() => {
            setSuccessItemId(0);
        }, 2000);
    }

    function mutateData(response: ResponseWrapper<OverviewDTO>) {
        mutate((prevData) => {
            if (!prevData) {
                return;
            } else {
                const modifiedOverviewPage = prevData.map((page) => {
                    const modifiedIndex = page.values.findIndex((object) => object.id == response.value.id);
                    if (modifiedIndex === -1) {
                        return page;
                    } else {
                        const modifiedValue: OverviewDTO = {
                            ...page.values[modifiedIndex],
                            approvedBy: response.value.approvedBy,
                            approvalDate: response.value.approvalDate,
                        };
                        return {
                            ...page,
                            values: [
                                ...page.values.slice(0, modifiedIndex),
                                modifiedValue,
                                ...page.values.slice(modifiedIndex + 1),
                            ],
                        };
                    }
                });
                return modifiedOverviewPage;
            }
        });
    }

    return (
        <>
            {emptyWithFilters && <NewTableEmptyResetFilter {...filters} />}
            {emptyWithoutFilters && renderComponentBasedOnFilter(filters)}
            {dto.data.map((item) => (
                <Row
                    key={item.id}
                    className={'atl-items-center'}
                    variant={item.id === successItemId ? 'success' : undefined}
                >
                    <RowColumns columns={columns} item={item} />
                    <div className={'sticky-action-cell'}>
                        <SalaryOverviewRowActions
                            approvedByDisplayed={isApprovedByDisplayed}
                            handleApproveUnapprove={(response) => {
                                if (response) {
                                    handleSuccess(item.id);
                                    mutateData(response);
                                }
                            }}
                            item={item}
                            intl={intl}
                            onClickDeleteVoucher={() => setConfirmDeleteById(item.id)}
                            onClickViewPreviewPdf={(isPayslip: boolean) => {
                                // Timeout to force it into the event loop to ensure the pdf viewer is ready
                                setTimeout(() => {
                                    setSelectedPdfItem({
                                        voucherId: item.voucherId ?? 0,
                                        transactionId: item.id,
                                        isPayslip: isPayslip,
                                    });
                                });
                            }}
                        />
                    </div>
                </Row>
            ))}
            <DeleteModal
                open={confirmDeleteById > 0}
                onClose={() => setConfirmDeleteById(0)}
                onConfirm={onConfirmDeleteVoucher}
                deleteStatus={deleteStatus}
            />
            {dto.isLoading && <SkeletonRows numberOfColumns={columns.length + 1} numberOfActions={2} />}
            {!dto.isLoading && dto.hasMore && (
                <LoadMoreTargetRow
                    hasMore={dto.hasMore}
                    onLoadMore={dto.loadMore}
                    scrollContainerRef={scrollableContainerRef}
                />
            )}
        </>
    );
};

const RowColumns = ({ columns, item }: { columns: ColumnType[]; item: OverviewDTO }) => {
    const intl = useIntl();
    return (
        <>
            <DisplayName item={item} />
            {columns.map((column) => {
                switch (column.id) {
                    case 'datePeriod':
                        return <TextCell key={column.id}>{item.datePeriod}</TextCell>;
                    case 'voucherDate':
                        return <TextCell key={column.id}>{item.voucherDate}</TextCell>;
                    case 'voucherNumberAsString':
                        return item.voucherNumberAsString ? (
                            <TextCell key={column.id}>{item.voucherNumberAsString}</TextCell>
                        ) : (
                            <TextCellDash key={column.id} />
                        );
                    case 'paymentDate':
                        return item.paymentDate ? (
                            <TextCell key={column.id}>
                                <div className="atl-flex atl-gap-8 atl-items-center">{item.paymentDate}</div>
                            </TextCell>
                        ) : (
                            <div key={column.id} className="atl-flex atl-gap-8 atl-items-center">
                                <TextCellDash />
                            </div>
                        );
                    case 'ameldingStatus':
                        return renderAmeldingStatus(item, intl, column.id);
                    case 'wagePeriodTransactionId':
                        return item.wagePeriodTransactionId > 0 ? (
                            <TextCell key={column.id}>
                                <a
                                    href={`/execute/wagePeriodTransactionMenu?wagePeriodTransactionId=${
                                        item.wagePeriodTransactionId
                                    }&contextId=${getContextId()}`}
                                >
                                    {item.wagePeriodTransactionVoucherNumber}
                                </a>
                            </TextCell>
                        ) : (
                            <TextCellDash key={column.id} />
                        );
                    case 'payrollTaxBasisAmount':
                        return <TlxNumberCell key={column.id}>{item.eniBasis}</TlxNumberCell>;
                    case 'approvedBy':
                        return item.approvedBy ? (
                            <Cell key={column.id}>
                                <Tooltip placement={'left'}>
                                    <TooltipTrigger>
                                        <div className="atl-flex atl-gap-8">
                                            <AvatarById
                                                name={item.approvedBy.displayName}
                                                imageId={item.approvedBy.pictureId}
                                            />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>{item.approvedBy.displayName}</TooltipContent>
                                </Tooltip>
                                <Tooltip placement={'right'}>
                                    <TooltipTrigger>
                                        <div className="atl-flex atl-gap-8 atl-items-center">
                                            {item.approvalDate === null
                                                ? ''
                                                : dateUtil.formatDate(item.approvalDate, 'no')}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {item.approvalDate === null
                                            ? ''
                                            : dateUtil.formatDate(item.approvalDate, 'no', 'yyyy-MM-dd HH:mm')}
                                    </TooltipContent>
                                </Tooltip>
                            </Cell>
                        ) : (
                            <TextCellDash key={column.id} />
                        );
                }
            })}
        </>
    );
};
const TextCellDash: React.FC = () => {
    return <TextCell key="dash">-</TextCell>;
};

const renderAmeldingStatus = (item: OverviewDTO, intl: IntlShape, key: Key) => {
    if (!item.ameldingStatus) {
        return <TextCellDash key={key} />;
    }
    const { variant, message, url } = handleAmeldingOverviewStatus(intl, item);

    return (
        <TextCell key={key}>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="atl-flex atl-items-center atl-gap-8
                atl-text-grey-100 taxCardMainTable-cursor-pointer"
            >
                <Status variant={variant} />
                {message}
            </a>
        </TextCell>
    );
};

function handleAmeldingOverviewStatus(intl: IntlShape, item: OverviewDTO) {
    let variant: StatusVariant = 'neutral';
    let message = item.ameldingDueDate;

    const dueDateObj = new Date(item.ameldingDueDate);

    switch (item.ameldingStatus) {
        case AmeldingOverviewStatus.NEUTRAL:
            break;

        case AmeldingOverviewStatus.SUCCESS:
            variant = 'success';
            message = intl.formatMessage({ id: 'text_ok' });
            break;

        case AmeldingOverviewStatus.WARNING:
            variant = 'warning';
            break;

        case AmeldingOverviewStatus.WARNING_GUIDELINE:
            variant = 'warning';
            message = intl.formatMessage({
                id: 'text_altinn_errors_severity_guideline',
            });
            break;

        case AmeldingOverviewStatus.ERROR:
            variant = 'error';
            break;

        case AmeldingOverviewStatus.ERROR_IMMEDIATELY:
            variant = 'error';
            message = intl.formatMessage({
                id: 'text_altinn_errors_severity_immediately',
            });
            break;

        case AmeldingOverviewStatus.ERROR_REJECTED:
            variant = 'error';
            message = intl.formatMessage({
                id: 'text_amelding_status_rejected',
            });
            break;
    }

    let url = `/execute/ameldingWageMenu?contextId=${getContextId()}`;
    if (message !== item.ameldingDueDate) {
        url += `&ameldingWageId=${item.ameldingWageId}`;
    } else {
        url += `&period.startDate=${dateUtil.formatDate(
            dateUtil.getFirstDayOfMonth(dateUtil.addMonths(dueDateObj, -1)),
            'en',
            'yyyy-MM-dd',
        )}&period.endOfPeriodDate=${dateUtil.formatDate(
            dateUtil.getLastDayOfMonth(dateUtil.addMonths(dueDateObj, -1)),
            'en',
            'yyyy-MM-dd',
        )}&period.periodType=4`;
    }

    return { variant, message, url };
}

interface ColumnListProps {
    columns: ColumnType[];
}

export const ColumnList: React.FC<ColumnListProps> = ({ columns }) => {
    const { formatMessage } = useIntl();
    const content = useSearchParam('content', 'transactions');
    let displayNameLabel;
    switch (content) {
        case 'pension':
            displayNameLabel = formatMessage({ id: 'text_pension' });
            break;
        case 'reimbursement':
            displayNameLabel = formatMessage({ id: 'text_sick_payable' });
            break;
        default:
            displayNameLabel = formatMessage({ id: 'text_wage_payment' });
            break;
    }
    return (
        <>
            <Column className="atl-whitespace-nowrap" sortable id="displayname" data-testid="column-displayname">
                {displayNameLabel}
            </Column>
            {columns.map((column) => (
                <Column
                    className="atl-whitespace-nowrap"
                    width={column.width}
                    key={column.id}
                    id={column.id}
                    sortable={column.id !== 'approvedBy'}
                    data-testid={`column-${column.id}`}
                    align={column.align}
                >
                    {column.label}
                </Column>
            ))}
        </>
    );
};

interface EmployeeColumnListProps extends ColumnListProps {
    isCheckedAll: boolean;
    onCheckAll: () => void;
    someChecked: boolean;
    selectAllState: SelectAllState;
}

function ShortColumnName({ name, shorten, width }: { name: string; shorten: boolean; width: string }) {
    if (!shorten) {
        return <span>{name}</span>;
    }
    return (
        <Tooltip>
            <TooltipTrigger>
                <span
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: width,
                    }}
                >
                    {name}
                </span>
            </TooltipTrigger>
            <TooltipContent>{name}</TooltipContent>
        </Tooltip>
    );
}

export const EmployeeColumnList: React.FC<EmployeeColumnListProps> = ({
    columns,
    isCheckedAll,
    onCheckAll,
    someChecked,
    selectAllState,
}) => {
    const indeterminateCheckboxRef = useIndeterminateCheckbox(someChecked);
    const { authLevelEmployeeOrWage } = useTskContext();

    return (
        <>
            {authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT && (
                <SelectAllColumns
                    selectAllState={selectAllState}
                    isCheckedAll={isCheckedAll}
                    onCheckAll={onCheckAll}
                    indeterminateCheckboxRef={indeterminateCheckboxRef}
                />
            )}
            <Column className="atl-whitespace-nowrap tsk-w-300" id="FIRSTNAME" data-testid="column-FIRSTNAME" sortable>
                <FormattedMessage id="text_employees" />
            </Column>
            {columns.map((column) => (
                <Column
                    className="atl-whitespace-nowrap"
                    width={column.width}
                    key={column.id}
                    id={column.id}
                    sortable
                    data-testid={`column-${column.id}`}
                    align={column.align}
                >
                    <ShortColumnName
                        name={column.label}
                        shorten={column.width?.endsWith('px') ?? false}
                        width={column.width ?? 'fit-content'}
                    />
                </Column>
            ))}
        </>
    );
};
